<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CustomAttribute from 'dashboard/components/CustomAttribute.vue';
import ClusterMemberRow from './ClusterMemberRow.vue';
import AddMemberPanel from './AddMemberPanel.vue';

const props = defineProps({
  clusterId: { type: [String, Number], required: true },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();

// ── Edit dialog ────────────────────────────────────────────────────────────────
const editDialogRef = ref(null);
const editName = ref('');

// ── Derived state ──────────────────────────────────────────────────────────────
const cluster = computed(() =>
  store.getters['clusters/getCluster'](props.clusterId)
);

const memberships = computed(() =>
  store.getters['clusterMemberships/getByCluster'](props.clusterId)
);

const roles = computed(
  () => cluster.value?.cluster_type?.member_roles || []
);

const clusterAttributeDefinitions = computed(() =>
  store.getters['attributes/getAttributesByModel']('cluster_attribute') || []
);

const clusterCustomAttributes = computed(
  () => cluster.value?.custom_attributes || {}
);

const uiFlags = computed(() => store.getters['clusters/getUIFlags']);

// ── Loaders ───────────────────────────────────────────────────────────────────
const loadCluster = () => store.dispatch('clusters/show', props.clusterId);

// ── Edit ──────────────────────────────────────────────────────────────────────
const openEdit = () => {
  editName.value = cluster.value?.name || '';
  editDialogRef.value?.open();
};

const saveEdit = async () => {
  const name = editName.value.trim();
  if (!name) return;
  try {
    await store.dispatch('clusters/update', { id: Number(props.clusterId), name });
    useAlert(t('CLUSTERS.API.UPDATE_SUCCESS'));
    editDialogRef.value?.close();
  } catch (_error) {
    useAlert(t('CLUSTERS.API.ERROR'));
  }
};

// ── Delete ────────────────────────────────────────────────────────────────────
const deleteCluster = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('CLUSTERS.DETAILS.DELETE_CONFIRM'))) return;
  await store.dispatch('clusters/delete', Number(props.clusterId));
  router.push({ name: 'clusters_list' });
};

// ── Attributes ────────────────────────────────────────────────────────────────
const onAttributeUpdate = async (key, value) => {
  const updated = { ...clusterCustomAttributes.value, [key]: value };
  await store.dispatch('clusters/update', {
    id: Number(props.clusterId),
    custom_attributes: updated,
  });
  loadCluster();
};

// ── Mount ─────────────────────────────────────────────────────────────────────
onMounted(() => {
  loadCluster();
  store.dispatch('clusterMemberships/get', props.clusterId);
  store.dispatch('attributes/get');
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <div class="flex-1 min-h-0 overflow-y-auto">
      <div class="flex flex-col w-full gap-6 px-6 py-6 lg:px-8">

        <!-- Back -->
        <Button
          variant="link"
          color="slate"
          size="sm"
          icon="i-lucide-arrow-left"
          :label="t('CLUSTERS.ACTIONS.BACK')"
          class="self-start"
          @click="router.push({ name: 'clusters_list' })"
        />

        <!-- Loading placeholder -->
        <template v-if="!cluster">
          <div class="flex items-center justify-center py-16">
            <span class="i-lucide-loader-circle animate-spin size-6 text-n-slate-10" />
          </div>
        </template>

        <template v-else>
          <!-- Header -->
          <header class="flex items-start justify-between gap-4">
            <div class="flex items-center min-w-0 gap-3">
              <Avatar :name="cluster.name" :size="48" />
              <div class="flex flex-col min-w-0">
                <h1 class="text-xl font-semibold truncate text-n-slate-12">
                  {{ cluster.name }}
                </h1>
                <p class="text-sm text-n-slate-10">
                  {{ cluster.cluster_type?.label_singular }}
                </p>
              </div>
            </div>
            <div class="flex items-center gap-2 shrink-0">
              <Button
                variant="faded"
                color="slate"
                size="sm"
                icon="i-lucide-pencil"
                :label="t('CLUSTERS.ACTIONS.EDIT')"
                @click="openEdit"
              />
              <Button
                variant="ghost"
                color="ruby"
                size="sm"
                icon="i-lucide-trash-2"
                :label="t('CLUSTERS.ACTIONS.DELETE')"
                :is-loading="uiFlags.isDeleting"
                @click="deleteCluster"
              />
            </div>
          </header>

          <!-- Body: main content + sidebar -->
          <div class="flex flex-col gap-6 lg:flex-row lg:items-start">

            <!-- Main: members -->
            <div class="flex flex-col flex-1 min-w-0 gap-6">
              <section
                class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2 sm:p-5"
              >
                <h2
                  class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
                >
                  <span class="i-lucide-users" />
                  {{ t('CLUSTERS.MEMBERS.TITLE') }}
                </h2>

                <!-- Member rows -->
                <div
                  v-if="memberships.length"
                  class="flex flex-col"
                >
                  <ClusterMemberRow
                    v-for="membership in memberships"
                    :key="membership.id"
                    :membership="membership"
                    :cluster-id="clusterId"
                    :roles="roles"
                  />
                </div>
                <p v-else class="text-sm text-n-slate-10 py-2">
                  {{ t('CLUSTERS.MEMBERS.EMPTY') }}
                </p>

                <!-- Add member panel -->
                <AddMemberPanel
                  :cluster-id="clusterId"
                  :roles="roles"
                  class="mt-2"
                />
              </section>
            </div>

            <!-- Sidebar: custom attributes -->
            <aside class="w-full lg:w-80 shrink-0">
              <section
                class="flex flex-col overflow-hidden border rounded-xl border-n-weak bg-n-solid-2"
              >
                <div
                  class="flex items-center gap-1.5 px-4 py-3 border-b border-n-weak text-xs font-semibold tracking-wide uppercase text-n-slate-10"
                >
                  <span class="i-lucide-sliders-horizontal" />
                  {{ t('CLUSTERS.SIDEBAR.ATTRIBUTES') }}
                </div>

                <div class="p-4">
                  <p
                    v-if="!clusterAttributeDefinitions.length"
                    class="text-sm text-n-slate-10"
                  >
                    {{ t('CLUSTERS.CUSTOM_FIELDS.EMPTY') }}
                  </p>
                  <div v-else class="flex flex-col gap-2">
                    <CustomAttribute
                      v-for="attribute in clusterAttributeDefinitions"
                      :key="attribute.id"
                      :label="attribute.attribute_display_name"
                      :description="attribute.attribute_description"
                      :attribute-key="attribute.attribute_key"
                      :attribute-type="attribute.attribute_display_type"
                      :values="attribute.attribute_values"
                      :value="clusterCustomAttributes[attribute.attribute_key] || ''"
                      :attribute-regex="attribute.regex_pattern"
                      :regex-cue="attribute.regex_cue"
                      show-actions
                      @update="onAttributeUpdate"
                    />
                  </div>
                </div>
              </section>
            </aside>
          </div>
        </template>
      </div>
    </div>

    <!-- Edit dialog -->
    <Dialog
      ref="editDialogRef"
      type="edit"
      width="lg"
      :title="t('CLUSTERS.EDIT_TITLE')"
      :confirm-button-label="t('CLUSTERS.ACTIONS.SAVE')"
      :disable-confirm-button="!editName.trim()"
      :is-loading="uiFlags.isUpdating"
      @confirm="saveEdit"
    >
      <div class="flex flex-col gap-4">
        <input
          v-model="editName"
          type="text"
          :aria-label="t('CLUSTERS.FIELDS.NAME')"
          :placeholder="t('CLUSTERS.NAME_PLACEHOLDER')"
          class="w-full text-lg font-semibold leading-snug bg-transparent rounded-md reset-base text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:bg-n-alpha-1 focus:px-2 focus:-mx-2 focus:py-1"
          @keyup.enter="saveEdit"
        />
      </div>
    </Dialog>
  </div>
</template>
